// The Email engine page (Session ASO, Items 1, 5 and 7): /office/email-engine and /office/email-engine/:id.
//
// Every inbound email the mailbox receives is recorded within minutes and given an outcome: drafted (a reply is waiting,
// normally as a draft in the Gmail conversation itself), needs you, or no reply needed. A message still undecided after
// the deadline is a FAULT and reads in red everywhere. This page is where the office sees all of it: the health of the
// engine (when it last read the mailbox, and any problem it hit), every recorded message with its outcome and reason,
// the reply library the drafts learn from, and how the drafts fared once people sent them.
//
// The server is server/src/modules/emailEngine/emailEngine.routes.ts; every read here is noCache (a live queue and a
// health strip must never show a cached state). Built on the same components and rules as EnquiriesPage.tsx and
// EnquiryDetailPage.tsx: a fixed-layout .ele-list-table that truncates (the TABLE RULE), client-side column sorting via
// useSortableColumn + SortableHeader (the SORT RULE; the list is paginated on the server, so a sort orders the page on
// screen), ConfirmButton / ConfirmDeleteModal for every delete and every consequential action (never window.confirm),
// and theme tokens only.
import { useCallback, useEffect, useRef, useState } from "react";
import { Pager } from "../../components/Pager";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type {
  EmailBackfillPlan,
  EmailBackfillStatus,
  EmailConversationPlan,
  EmailEngineDraftPreview,
  EmailEngineDraftResponse,
  EmailEngineExample,
  EmailEngineFacts,
  EmailEngineMessageDetail,
  EmailEngineMessageRow,
  EmailEnginePollResult,
  EmailEngineStatus,
  EmailEngineValidationFailure,
  EmailEngineGateLog,
  EmailQualityReport,
  EmailStandardLineRow,
  ReplyLibraryResponse,
  ReplyLibrarySyncResult,
} from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { PageHeader } from "../../components/PageHeader";
import { RecordHeader } from "../../components/RecordHeader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { SortableHeader } from "../../components/SortableHeader";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { ATTACHMENT_LABEL, CHECK_LABEL, EDIT_KIND_LABEL, gmailThreadHref, outcomeDisplay, senderLine, draftSourceLabel, COMPANY_SIGNATURE_LABEL } from "./emailEngineUi";
import { formatDate, formatDateTime } from "../../lib/format";
import { AgentRunSection, AgentShadowSection } from "./EmailAgentPanels";

const PAGE_SIZE = 50;
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const inputCls =
  "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-70";
const chipCls = (on: boolean) =>
  `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    on ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
  }`;
const ROW_ACTION = "font-menu text-xs tracking-wide transition hover:text-ele-strong";
// A two-step confirm styled as the secondary button beside it (the Mailbox history controls).
const CONFIRM_BUTTON_CLS =
  "inline-flex items-center rounded-ele border border-ele-green px-3 py-1.5 font-heading text-xs tracking-wide text-ele-green transition hover:bg-ele-green hover:text-ele-bg max-sm:min-h-11";
// The theme's own ink tokens (ele-theme.css), never a hardcoded hex: amber ink and the danger red both resolve to a
// value that reads on the ground they sit on, dark or light (Session ASO review, house rules LOW 6).
const AMBER_TEXT = "text-[var(--ele-amber-text)]";
const RED_TEXT = "text-ele-danger";

type Tab = "messages" | "library" | "quality" | "history";
const TABS: { value: Tab; label: string }[] = [
  { value: "messages", label: "Messages" },
  { value: "library", label: "Library" },
  { value: "quality", label: "Draft quality" },
  { value: "history", label: "Mailbox history" },
];

// Session ATZ: "filed" and "work" are the two lanes (a message filed under a Gmail label, a work order), not outcomes.
// Session AUA: "reply_overdue" is past the 24-hour first-reply target (DE-5), a different clock from the engine's own
// "overdue" (a message with no outcome past its deadline, shown as Faults).
type MessageFilter = "all" | "needs_you" | "drafted" | "no_reply_needed" | "overdue" | "reply_overdue" | "filed" | "work";
const MESSAGE_FILTERS: MessageFilter[] = ["all", "needs_you", "drafted", "no_reply_needed", "overdue", "reply_overdue", "filed", "work"];

function isMessageFilter(v: string | null): v is MessageFilter {
  return v != null && (MESSAGE_FILTERS as string[]).includes(v);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export default function EmailEnginePage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = tabParam === "library" || tabParam === "quality" || tabParam === "history" ? tabParam : "messages";

  function selectTab(next: Tab) {
    const p = new URLSearchParams(params);
    if (next === "messages") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  }

  return (
    <div>
      <PageHeader
        title="Email engine"
        subtitle="Every email that reaches the mailbox is recorded within minutes and given an outcome: a reply drafted for you in Gmail, a message that needs you, or one that needs no reply."
        className="mb-4"
      />
      <div role="tablist" aria-label="Email engine sections" className="mb-4 flex flex-wrap gap-2 border-b border-ele-hairline pb-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => selectTab(t.value)}
            className={chipCls(tab === t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "messages" && <MessagesTab />}
      {tab === "library" && <LibraryTab />}
      {tab === "quality" && <QualityTab />}
      {tab === "history" && <HistoryTab />}
    </div>
  );
}

// ---- Messages ---------------------------------------------------------------------------------------------------------

function MessagesTab() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filterParam = params.get("filter");
  const filter: MessageFilter = isMessageFilter(filterParam) ? filterParam : "all";

  const [status, setStatus] = useState<EmailEngineStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [messages, setMessages] = useState<EmailEngineMessageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const sort = useSortableColumn("email-engine-messages");
  const seq = useRef(0);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.emailEngine.status());
      setStatusError(null);
    } catch (err) {
      setStatusError(friendlyApiError(err, "Could not read the email engine's status."));
    }
  }, []);

  const loadMessages = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setListError(null);
    try {
      const res = await api.emailEngine.messages({
        outcome: filter === "all" || filter === "filed" || filter === "work" ? undefined : filter,
        lane: filter === "filed" ? "file" : filter === "work" ? "work" : undefined,
        search: search.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (mine !== seq.current) return;
      setMessages(res.messages);
      setTotal(res.total);
    } catch (err) {
      if (mine === seq.current) setListError(friendlyApiError(err, "Could not load the messages."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [filter, search, offset]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  function selectFilter(next: MessageFilter) {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("filter");
    else p.set("filter", next);
    setParams(p, { replace: true });
    setOffset(0);
  }

  const counts = status?.last24h;
  const chipCount: Partial<Record<MessageFilter, number>> = counts
    ? {
        all: counts.drafted + counts.needsYou + counts.noReplyNeeded + counts.pending,
        needs_you: counts.needsYou,
        drafted: counts.drafted,
        no_reply_needed: counts.noReplyNeeded,
      }
    : {};
  const chipLabel: Record<MessageFilter, string> = {
    all: "All",
    needs_you: "Needs you",
    drafted: "Drafted",
    no_reply_needed: "No reply needed",
    overdue: "Faults",
    reply_overdue: "Reply overdue (24h target)",
    filed: "Filed",
    work: "Work orders",
  };

  const accessors: SortAccessors<EmailEngineMessageRow> = {
    received: (m) => m.receivedAt,
    from: (m) => senderLine(m),
    subject: (m) => m.subject,
    outcome: (m) => outcomeDisplay(m).word,
    reason: (m) => m.outcomeReason,
    category: (m) => m.categoryLabel,
  };
  const rows = sortRows(messages, accessors, sort);

  return (
    <div>
      <StatusStrip
        status={status}
        error={statusError}
        onChecked={() => {
          void loadStatus();
          void loadMessages();
        }}
        onDraftingChanged={() => void loadStatus()}
      />

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          placeholder="Search subject or sender..."
          aria-label="Search messages"
          className="w-full min-w-0 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong placeholder:text-ele-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green sm:max-w-xs"
        />
        <div className="flex flex-wrap items-center gap-2">
          {MESSAGE_FILTERS.map((f) => {
            const n = f === "overdue" ? status?.overdue : f === "reply_overdue" ? status?.replyOverdue : chipCount[f];
            const alarm = (f === "overdue" || f === "reply_overdue") && n != null && n > 0;
            return (
              <button key={f} type="button" aria-pressed={filter === f} onClick={() => selectFilter(f)} className={chipCls(filter === f)}>
                {chipLabel[f]}
                {n != null && <span className={`ml-1 ${alarm ? (f === "overdue" ? RED_TEXT : AMBER_TEXT) : "opacity-70"}`}>({n})</span>}
              </button>
            );
          })}
        </div>
      </div>
      {counts && <p className="mb-3 text-xs text-ele-body opacity-80">The counts beside the filters are for the last 24 hours; the list shows every recorded message.</p>}
      <AddConversationPanel onAdded={() => void loadMessages()} />

      {loading && messages.length === 0 ? (
        <Loader label="Loading the messages..." rows={6} />
      ) : listError ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{listError}</p>
          <button type="button" onClick={() => void loadMessages()} className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong">
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          message={search.trim() ? `No messages match "${search.trim()}".` : filter === "all" ? "No messages recorded yet." : "No messages match this filter."}
          hint={filter === "all" && !search.trim() ? "Messages appear here within minutes of arriving in the mailbox." : undefined}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm">
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "12%" }} />
                <col className="max-md:hidden" style={{ width: "22%" }} />
                <col className="max-md:hidden" style={{ width: "12%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <SortableHeader label="Received" colKey="received" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="From" colKey="from" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Subject" colKey="subject" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Outcome" colKey="outcome" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Reason" colKey="reason" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                  <SortableHeader label="Category" colKey="category" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const pill = outcomeDisplay(m);
                  const source = draftSourceLabel({ kind: m.draftKind, templateKey: m.templateKey, templateVersion: m.templateVersion });
                  return (
                    <tr
                      key={m.id}
                      data-testid={`message-${m.id}`}
                      className="cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-inset"
                      onClick={() => navigate(`/office/email-engine/${encodeURIComponent(m.id)}`)}
                    >
                      <td className="truncate px-3 py-2 align-top">{formatDateTime(m.receivedAt)}</td>
                      <td className="truncate px-3 py-2 align-top" title={senderLine(m)}>
                        {m.fromName || m.from || "Unknown sender"}
                      </td>
                      <td className="truncate px-3 py-2 align-top text-ele-strong" title={m.subject ?? ""}>
                        {m.subject?.trim() || "(no subject)"}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <StatusPill word={pill.word} tone={pill.tone} />
                        {m.replyOverdue && (
                          <div className="mt-1" data-testid={`reply-overdue-${m.id}`}>
                            <StatusPill word="Reply overdue (24h target)" tone="amber" title="A first reply is due within 24 hours for this kind of enquiry" />
                          </div>
                        )}
                        {m.lateReply && (
                          <div className="mt-1" data-testid={`late-reply-${m.id}`}>
                            <StatusPill word={m.lateReply} tone="amber" title="Drafted on a later working day than the email arrived, so it opens with the fixed apology" />
                          </div>
                        )}
                        {source && (
                          <div className="mt-1 truncate text-xs text-ele-body" data-testid={`draft-source-${m.id}`} title={source}>
                            {source}
                          </div>
                        )}
                      </td>
                      <td className="max-md:hidden truncate px-3 py-2 align-top text-ele-body" title={m.outcomeReason ?? ""}>
                        {m.outcomeReason ?? "-"}
                      </td>
                      <td className="max-md:hidden truncate px-3 py-2 align-top text-ele-body">
                        {m.lane === "file" ? `Filed: ${m.fileKindLabel ?? "filed"}` : m.lane === "work" ? "Work order" : (m.categoryLabel ?? "-")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} total={total} shown={messages.length} pageSize={PAGE_SIZE} onOffset={setOffset} noun={["message", "messages"]} />
        </>
      )}
    </div>
  );
}

// Session ATZ (Item 5): add a conversation the engine missed, by its Gmail id. The check says what would be recorded and
// whether it will show in the Enquiries queue; the add records it through the engine's one recorder, after which the
// engine decides it like any other message (at most a Gmail draft, never a send). Adding one already recorded changes
// nothing.
export function gmailIdFrom(text: string): { gmailThreadId: string } | null {
  const t = text.trim();
  // A bare id, or the hex id at the end of a pasted Gmail address (the long code after the last slash).
  const m = /^([0-9a-f]{10,24})$/i.exec(t) ?? /\/([0-9a-f]{10,24})(?:[?#].*)?$/i.exec(t);
  return m ? { gmailThreadId: m[1]!.toLowerCase() } : null;
}

function AddConversationPanel({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [plan, setPlan] = useState<EmailConversationPlan | null>(null);
  const [busy, setBusy] = useState<"plan" | "add" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function check() {
    const ids = gmailIdFrom(text);
    if (!ids) {
      setError("Paste the conversation's Gmail id: the long code of letters and numbers at the end of its Gmail address.");
      return;
    }
    setBusy("plan");
    setError(null);
    setDone(null);
    try {
      setPlan((await api.emailEngine.planAddConversation(ids)).plan);
    } catch (err) {
      setPlan(null);
      setError(friendlyApiError(err, "Could not read that conversation."));
    } finally {
      setBusy(null);
    }
  }

  async function add() {
    if (!plan) return;
    setBusy("add");
    setError(null);
    try {
      const { result } = await api.emailEngine.addConversation({ gmailThreadId: plan.gmailThreadId });
      setDone(
        result.recorded > 0
          ? `Recorded ${plural(result.recorded, "message", "messages")}, and the engine has decided ${result.recorded === 1 ? "it" : "them"}: see the list below.${result.queueNote ? ` ${result.queueNote}` : ""}`
          : "Every message in that conversation was already on record, so nothing changed.",
      );
      setPlan(null);
      setText("");
      onAdded();
    } catch (err) {
      setError(friendlyApiError(err, "Could not add that conversation."));
    } finally {
      setBusy(null);
    }
  }

  if (!open) {
    return (
      <p className="mb-3 text-xs text-ele-body">
        A conversation missing from this list?{" "}
        <button type="button" onClick={() => setOpen(true)} className={`${ROW_ACTION} text-ele-blue`}>
          Add it by its Gmail id
        </button>
      </p>
    );
  }
  return (
    <section className={sectionCls} aria-label="Add a conversation the engine missed">
      <h2 className="mb-1 font-heading text-base text-ele-strong">Add a conversation the engine missed</h2>
      <p className="mb-3 text-sm text-ele-body">
        Paste the conversation&rsquo;s Gmail id, or its Gmail address ending with the id. It is recorded exactly as the engine records new mail,
        and the engine then decides it: a draft reply in Gmail, needs you, or no reply needed. Nothing is ever sent.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-64 flex-1">
          <span className={labelCls}>Gmail id</span>
          <input
            type="text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPlan(null);
              setError(null);
            }}
            placeholder="18f2c0a9b7d3e4f1"
            aria-label="Gmail id"
            className={inputCls}
          />
        </label>
        <button type="button" onClick={() => void check()} disabled={busy != null || !text.trim()} className={chipCls(false)}>
          {busy === "plan" ? "Reading..." : "Check"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className={`${ROW_ACTION} text-ele-body`}>
          Close
        </button>
      </div>
      {plan && (
        <div className="mt-3 text-sm">
          <p className="text-ele-strong">{plan.subject ?? "(no subject)"}</p>
          <ul className="mt-1 space-y-0.5 text-xs text-ele-body">
            {plan.messages.map((m) => (
              <li key={m.gmailMessageId}>
                {formatDateTime(m.receivedAt)} from {m.from ?? "an unreadable sender"}
                {m.alreadyRecorded ? " (already recorded)" : ""}
              </li>
            ))}
          </ul>
          {plan.queueNote && <p className="mt-1 text-xs text-ele-body">{plan.queueNote}</p>}
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => void add()} disabled={busy != null || plan.toRecord === 0}>
            {busy === "add" ? "Adding..." : plan.toRecord === 0 ? "Already recorded" : `Record ${plural(plan.toRecord, "message", "messages")}`}
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="mt-2 text-sm text-ele-strong">
          {done}
        </p>
      )}
    </section>
  );
}

// The engine's health: when it last read the mailbox (amber when it has stopped), the faults in red, Check now, and the
// automatic drafting switch. Never a reassuring "all clear" when nothing verified it.
export function StatusStrip({
  status,
  error,
  onChecked,
  onDraftingChanged,
}: {
  status: EmailEngineStatus | null;
  error: string | null;
  onChecked: () => void;
  onDraftingChanged: () => void;
}) {
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<EmailEnginePollResult | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchingGate, setSwitchingGate] = useState(false);

  async function checkNow() {
    setChecking(true);
    setCheckError(null);
    setCheckResult(null);
    try {
      setCheckResult(await api.emailEngine.poll());
      onChecked();
    } catch (err) {
      setCheckError(friendlyApiError(err, "The mailbox could not be checked. Please try again."));
    } finally {
      setChecking(false);
    }
  }

  let health: ReactNode;
  if (!status) {
    health = <span className="text-ele-body">{error ?? "Reading the engine's status..."}</span>;
  } else if (!status.started) {
    health = <span className={AMBER_TEXT}>Not started yet. The engine starts on its first check of the mailbox.</span>;
  } else if (status.stale) {
    health = <span className={AMBER_TEXT}>Not checked since {formatDateTime(status.polledAt)}. New mail may be waiting unseen.</span>;
  } else {
    health = <span className="text-ele-strong">Last checked {formatDateTime(status.polledAt)}</span>;
  }

  return (
    <section aria-label="Email engine status" className={`${sectionCls} flex flex-col gap-3`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1 text-sm">
          <p data-testid="engine-health">{health}</p>
          {status?.pollError && <p className={`text-xs ${AMBER_TEXT}`}>The last check hit a problem: {status.pollError}</p>}
          {status?.sweepError && (
            <p data-testid="sweep-error" className={`text-xs ${AMBER_TEXT}`}>
              The hourly check could not read everything: {status.sweepError}
            </p>
          )}
          {status && status.overdue > 0 && (
            <p data-testid="engine-faults" className={`font-semibold ${RED_TEXT}`}>
              {status.overdue === 1 ? "1 message has" : `${status.overdue} messages have`} waited more than {status.deadlineMinutes} minutes with no outcome.
            </p>
          )}
          {/* Session AUA (DE-5): modernisation, new installation and maintenance quote enquiries get a first reply within 24 hours. */}
          {status?.replyOverdue != null && status.replyOverdue > 0 && (
            <p data-testid="engine-reply-overdue" className={`text-sm ${AMBER_TEXT}`}>
              {status.replyOverdue === 1 ? "1 enquiry has" : `${status.replyOverdue} enquiries have`} waited more than 24 hours for a first reply.
            </p>
          )}
          {status?.pendingTemplateProposals != null && status.pendingTemplateProposals > 0 && (
            <p data-testid="engine-template-proposals" className="text-xs text-ele-body">
              <Link to="/office/reply-templates?tab=proposals" className="text-ele-blue underline-offset-2 hover:underline">
                {status.pendingTemplateProposals === 1 ? "1 reply template proposal is" : `${status.pendingTemplateProposals} reply template proposals are`} waiting for a decision.
              </Link>
            </p>
          )}
          {status && (
            <p className="text-xs text-ele-body">
              Automatic drafting is <strong className="text-ele-strong">{status.draftingEnabled ? "on" : "off"}</strong>
              {status.draftingEnabled ? "." : ". New messages are recorded and sorted, but none is drafted."}
            </p>
          )}
          {status?.engineMode && (
            <p data-testid="engine-agent-mode" className="text-xs text-ele-body">
              Live replies are written by <strong className="text-ele-strong">{status.engineMode === "agent" ? "the investigating agent" : "the classic engine"}</strong>
              {status.engineMode === "agent"
                ? "."
                : status.agentShadow === false
                  ? ". The agent is not running in the background."
                  : ". The investigating agent also runs in the background on each new message and its answer is kept beside the draft for comparison; nothing it writes is sent."}
            </p>
          )}
          {status && (
            <p data-testid="engine-sentence-gate" className="text-xs text-ele-body">
              The sentence gate is <strong className="text-ele-strong">{status.sentenceGateMode === "enforce" ? "enforcing" : "log only"}</strong>
              {status.sentenceGateMode === "enforce"
                ? ": a statement it cannot match to a record or the customer's message is removed from the draft."
                : ": it records the statements it would have removed (shown on each draft's record) but removes nothing."}
            </p>
          )}
        </div>
        <div className="flex min-w-min max-w-full flex-wrap items-center gap-2 [&>a]:whitespace-nowrap [&>button]:whitespace-nowrap">
          <Button variant="secondary" size="sm" onClick={() => void checkNow()} disabled={checking}>
            {checking ? "Checking..." : "Check now"}
          </Button>
          {status && (
            <Button variant="ghost" size="sm" onClick={() => setSwitching(true)}>
              {status.draftingEnabled ? "Switch automatic drafting off" : "Switch automatic drafting on"}
            </Button>
          )}
          {status && (
            <Button variant="ghost" size="sm" onClick={() => setSwitchingGate(true)}>
              {status.sentenceGateMode === "enforce" ? "Make the sentence gate log only" : "Make the sentence gate enforcing"}
            </Button>
          )}
        </div>
      </div>
      {checkResult && (
        <p role="status" className="text-xs text-ele-body">
          Checked. {plural(checkResult.inboundRecorded, "new message", "new messages")} recorded
          {checkResult.processed ? `, ${plural(checkResult.processed.decided, "given an outcome", "given an outcome")}` : ""}
          {checkResult.sentHandled > 0 ? `, ${plural(checkResult.sentHandled, "sent reply", "sent replies")} noted` : ""}.
          {checkResult.readFailures > 0 ? ` ${plural(checkResult.readFailures, "conversation", "conversations")} could not be read and will be retried.` : ""}
          {checkResult.truncated ? " There was more mail than one check reads; the rest follows on the next check." : ""}
        </p>
      )}
      {checkError && (
        <p role="alert" className={`text-xs ${RED_TEXT}`}>
          {checkError}
        </p>
      )}
      {switching && status && (
        <DraftingSwitchModal
          currentlyOn={status.draftingEnabled}
          onClose={() => setSwitching(false)}
          onDone={() => {
            setSwitching(false);
            onDraftingChanged();
          }}
        />
      )}
      {switchingGate && status && (
        <GateSwitchModal
          currentlyEnforcing={status.sentenceGateMode === "enforce"}
          onClose={() => setSwitchingGate(false)}
          onDone={() => {
            setSwitchingGate(false);
            onDraftingChanged();
          }}
        />
      )}
    </section>
  );
}

// Session BCR2 (Director decision D-BCR1-1): the sentence gate's switch, the same two-step branded confirm as the drafting
// switch. Log only is the default; the gate's mapping rule is the same in both modes, only whether its verdict is applied.
function GateSwitchModal({ currentlyEnforcing, onClose, onDone }: { currentlyEnforcing: boolean; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = currentlyEnforcing ? "Make the sentence gate log only?" : "Make the sentence gate enforcing?";
  const message = currentlyEnforcing
    ? "The gate will keep recording the statements it would have removed from each free draft, shown on the draft's record, but it will remove nothing and hold nothing. Every other check still applies, so statements the records do not support will reach your drafts more often: read each draft before sending."
    : "A statement the gate cannot match to a record or the customer's message will be removed from each free draft, and a draft left unable to answer a question will be held for you with the gap named. Session BCR1 measured this mode as removing more good sentences than bad.";

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.emailEngine.setSentenceGate(!currentlyEnforcing);
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "The setting could not be changed. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()}>
      <h2 className="font-heading text-xl text-ele-strong">{title}</h2>
      <p className="mt-2 text-sm text-ele-body">{message}</p>
      {error && (
        <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Saving..." : currentlyEnforcing ? "Yes, log only" : "Yes, enforce it"}
        </Button>
      </div>
    </ModalShell>
  );
}

// The drafting switch: a branded confirm that says exactly what it does. The first press opens it; the second, inside
// it, makes the change.
function DraftingSwitchModal({ currentlyOn, onClose, onDone }: { currentlyOn: boolean; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const title = currentlyOn ? "Switch automatic drafting off?" : "Switch automatic drafting on?";
  const message = currentlyOn
    ? "New messages will still be recorded and sorted, but none will be drafted until you switch it back on."
    : "New messages that need a reply will be drafted again, and each draft will be put in the Gmail conversation for you to check.";

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.emailEngine.setDrafting(!currentlyOn);
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "The setting could not be changed. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()}>
      <h2 className="font-heading text-xl text-ele-strong">{title}</h2>
      <p className="mt-2 text-sm text-ele-body">{message}</p>
      {error && (
        <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Saving..." : currentlyOn ? "Yes, switch it off" : "Yes, switch it on"}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- Mailbox history: the all-time backfill (Session ATX, Item 5) ------------------------------------------------------
// The engine records live mail from the moment it started. This reads everything older, from the mailbox's first day, and
// gives each message an outcome the same way. It runs only when a person starts it here (or from chat), in batches, and
// can be paused, stopped or reset. Start and stop each show the server's own preview first, then a confirm.

export function HistoryTab() {
  const [status, setStatus] = useState<EmailBackfillStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [modal, setModal] = useState<null | "start" | "stop">(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setStatus(await api.emailEngine.backfill());
    } catch (err) {
      setLoadError(friendlyApiError(err, "Could not read the mailbox history's progress."));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function act(run: () => Promise<unknown>, done: string, fallback: string) {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await run();
      setNotice(done);
      await load();
    } catch (err) {
      setActionError(friendlyApiError(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return loadError ? (
      <p role="alert" className={`text-sm ${RED_TEXT}`}>
        {loadError}
      </p>
    ) : (
      <p className="text-sm text-ele-body">Reading the mailbox history's progress...</p>
    );
  }

  const s = status.status;
  const percent = status.windows.total > 0 ? Math.round((status.windows.done / status.windows.total) * 100) : 0;
  const decided = status.outcomes.drafted + status.outcomes.needsYou + status.outcomes.noReplyNeeded;

  return (
    <div>
      <section aria-label="Mailbox history" className={sectionCls}>
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Mailbox history</h2>
        <p className="text-sm text-ele-body">
          The engine records new mail from the moment it started. This reads the mailbox from its first day up to then, and gives
          every older message an outcome in the same way. A conversation already answered needs no reply; one still owed a reply
          gets a draft in Gmail when automatic drafting is on. Nothing is ever sent.
        </p>
        <p data-testid="history-headline" className="mt-3 text-sm text-ele-strong">
          {status.headline}
        </p>
        {status.from && status.until && (
          <div className="mt-3" aria-label="Progress">
            <div className="h-2 w-full overflow-hidden rounded-full bg-ele-inset">
              <div className="h-2 rounded-full bg-ele-green" style={{ width: `${percent}%` }} />
            </div>
            <p className="mt-1 text-xs text-ele-body">
              {percent}% read, from {formatDate(status.from)} to {formatDate(status.until)}
              {status.cursor && s !== "complete" ? `; read up to ${formatDate(status.cursor)}` : ""}.
            </p>
          </div>
        )}
        {status.recorded > 0 && (
          <p className="mt-2 text-xs text-ele-body" data-testid="history-counts">
            {plural(status.recorded, "message", "messages")} recorded, {decided} given an outcome: {status.outcomes.drafted} drafted,{" "}
            {status.outcomes.needsYou} need you, {status.outcomes.noReplyNeeded} need no reply
            {status.outcomes.pending > 0 ? `, ${status.outcomes.pending} still to decide` : ""}.
          </p>
        )}
        {status.lastBatchAt && <p className="mt-1 text-xs text-ele-body">Last batch {formatDateTime(status.lastBatchAt)}.</p>}
        {status.startedAt && (
          <p className="mt-1 text-xs text-ele-body">
            Started {formatDateTime(status.startedAt)}
            {status.startedBy ? ` by ${status.startedBy}` : ""}.
          </p>
        )}
        {status.error && (
          <p data-testid="history-error" className={`mt-2 text-xs ${AMBER_TEXT}`}>
            {status.error}
          </p>
        )}
        {!status.draftingEnabled && s !== "complete" && (
          <p className={`mt-2 text-xs ${AMBER_TEXT}`}>Automatic drafting is off, so a message still owed a reply is marked as needing you instead of drafted.</p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {status.engineStartedAt && (s === null || s === "paused" || s === "stopped") && (
            <Button variant="primary" size="sm" onClick={() => setModal("start")} disabled={busy}>
              {s === null ? "Start reading the mailbox history" : "Resume"}
            </Button>
          )}
          {s === "running" && (
            <ConfirmButton
              label="Pause"
              className={CONFIRM_BUTTON_CLS}
              confirmLabel="Pause the reading?"
              busyLabel="Pausing..."
              busy={busy}
              onConfirm={() => void act(() => api.emailEngine.pauseBackfill(), "Paused. Nothing is lost; Resume carries on from where it reached.", "It could not be paused.")}
            />
          )}
          {(s === "running" || s === "paused") && (
            <Button variant="ghost" size="sm" onClick={() => setModal("stop")} disabled={busy}>
              Stop
            </Button>
          )}
          {(s === "stopped" || s === "complete") && (
            <ConfirmButton
              label="Reset"
              className={CONFIRM_BUTTON_CLS}
              confirmLabel="Clear the history's settings?"
              busyLabel="Resetting..."
              busy={busy}
              onConfirm={() =>
                void act(
                  () => api.emailEngine.resetBackfill(),
                  "Reset. The messages it recorded stay; a fresh start reads the mailbox from the beginning again.",
                  "It could not be reset.",
                )
              }
            />
          )}
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={busy}>
            Refresh
          </Button>
        </div>
        {!status.engineStartedAt && <p className="mt-2 text-xs text-ele-body">The engine has not started yet, so there is nothing to read back to.</p>}
        {notice && (
          <p role="status" className="mt-3 text-xs text-ele-body">
            {notice}
          </p>
        )}
        {actionError && (
          <p role="alert" className={`mt-3 text-xs ${RED_TEXT}`}>
            {actionError}
          </p>
        )}
      </section>
      {modal === "start" && (
        <StartHistoryModal
          onClose={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setNotice(msg);
            void load();
          }}
        />
      )}
      {modal === "stop" && (
        <StopHistoryModal
          onClose={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setNotice(msg);
            void load();
          }}
        />
      )}
    </div>
  );
}

// Start or resume: the server's own preview (what would be read, and what it will do), then a confirm.
function StartHistoryModal({ onClose, onDone }: { onClose: () => void; onDone: (notice: string) => void }) {
  const [plan, setPlan] = useState<EmailBackfillPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.emailEngine
      .startBackfill({ preview: true })
      .then((res) => live && setPlan(res.plan))
      .catch((err) => live && setError(friendlyApiError(err, "The reading cannot be started.")));
    return () => {
      live = false;
    };
  }, []);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.emailEngine.startBackfill({ preview: false });
      onDone(plan?.resume ? "Resumed. Batches carry on from where it reached." : "Started. The first batch runs within a few minutes.");
    } catch (err) {
      setError(friendlyApiError(err, "The reading was not started."));
      setBusy(false);
    }
  }

  const title = plan?.resume ? "Resume reading the mailbox history?" : "Read the mailbox history?";
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()}>
      <h2 className="font-heading text-xl text-ele-strong">{title}</h2>
      {!plan && !error && <p className="mt-2 text-sm text-ele-body">Working out what would be read...</p>}
      {plan && (
        <>
          <p data-testid="history-plan" className="mt-2 text-sm text-ele-body">
            {plan.wouldDo}
          </p>
          <p className="mt-2 text-xs text-ele-body">
            {plan.windowsRemaining} of {plan.windowsTotal} stretches of two days to read. You can pause or stop it at any time.
          </p>
        </>
      )}
      {error && (
        <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => void confirm()} disabled={busy || !plan}>
          {busy ? "Starting..." : plan?.resume ? "Yes, resume" : "Yes, start reading"}
        </Button>
      </div>
    </ModalShell>
  );
}

// Stop: says how many recorded messages are still undecided (they will be marked as needing you), then a confirm.
function StopHistoryModal({ onClose, onDone }: { onClose: () => void; onDone: (notice: string) => void }) {
  const [wouldSettle, setWouldSettle] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.emailEngine
      .stopBackfill(true)
      .then((res) => live && setWouldSettle(res.wouldSettle ?? 0))
      .catch((err) => live && setError(friendlyApiError(err, "The reading cannot be stopped.")));
    return () => {
      live = false;
    };
  }, []);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.emailEngine.stopBackfill(false);
      const n = res.settled ?? 0;
      onDone(`Stopped.${n > 0 ? ` ${plural(n, "message", "messages")} not yet decided ${n === 1 ? "was" : "were"} marked as needing you.` : ""}`);
    } catch (err) {
      setError(friendlyApiError(err, "The reading was not stopped."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Stop reading the mailbox history?" onClose={() => !busy && onClose()}>
      <h2 className="font-heading text-xl text-ele-strong">Stop reading the mailbox history?</h2>
      <p className="mt-2 text-sm text-ele-body">No further batches run. The messages it has recorded stay on record.</p>
      {wouldSettle !== null && (
        <p data-testid="history-stop-preview" className="mt-2 text-sm text-ele-body">
          {wouldSettle > 0
            ? `${plural(wouldSettle, "recorded message has", "recorded messages have")} not been decided yet; ${wouldSettle === 1 ? "it" : "they"} will be marked as needing you.`
            : "Every message it recorded has already been decided."}
        </p>
      )}
      {error && (
        <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
          {error}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => void confirm()} disabled={busy || wouldSettle === null}>
          {busy ? "Stopping..." : "Yes, stop it"}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- Library ----------------------------------------------------------------------------------------------------------

export function LibraryTab() {
  const [data, setData] = useState<ReplyLibraryResponse | null>(null);
  const [status, setStatus] = useState<EmailEngineStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<ReplyLibrarySyncResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.emailEngine.library({ category: category || undefined, search: search.trim() || undefined, limit: PAGE_SIZE, offset });
      if (mine === seq.current) setData(res);
    } catch (err) {
      if (mine === seq.current) setError(friendlyApiError(err, "Could not load the reply library."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [category, search, offset]);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.emailEngine.status());
    } catch {
      // The library's own list still shows; only the "how far back" line is missing.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function sync() {
    setSyncing(true);
    setActionError(null);
    setSyncResult(null);
    setNotice(null);
    try {
      setSyncResult(await api.emailEngine.syncLibrary());
      void load();
      void loadStatus();
    } catch (err) {
      setActionError(friendlyApiError(err, "The library could not be brought up to date. Please try again."));
    } finally {
      setSyncing(false);
    }
  }

  async function remove(id: string) {
    setRemovingId(id);
    setActionError(null);
    try {
      await api.emailEngine.removeExample(id);
      setNotice("The example was removed from the library.");
      void load();
    } catch (err) {
      setActionError(friendlyApiError(err, "Could not remove that example."));
    } finally {
      setRemovingId(null);
    }
  }

  const reachedBack = syncResult?.backfillBefore ?? status?.library.backfillReachedBack ?? null;

  return (
    <div>
      <section className={sectionCls}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-sm text-ele-body">
            <p className="text-ele-strong">
              The library holds the replies the office has actually sent. Each new draft is written from the closest past replies of the same kind.
            </p>
            {status && (
              <p className="mt-1 text-xs">
                {plural(status.library.total, "example", "examples")}, {status.library.categorised} sorted into a kind.{" "}
                {reachedBack ? `It reaches back to ${formatDate(reachedBack)}.` : "Reading past replies has not started yet."}
                {status.library.syncedAt ? ` Sent mail last read ${formatDateTime(status.library.syncedAt)}.` : ""}
                {status.library.categorisedAt ? ` Last sorted ${formatDateTime(status.library.categorisedAt)}.` : ""}
              </p>
            )}
            {status?.library.syncError && (
              <p data-testid="library-sync-error" className={`mt-1 text-xs ${AMBER_TEXT}`}>
                The last sync did not finish every step: {status.library.syncError}
              </p>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={() => void sync()} disabled={syncing}>
            {syncing ? "Syncing..." : "Sync now"}
          </Button>
        </div>
        {syncResult && (
          <p role="status" className="mt-3 text-xs text-ele-body">
            Added {plural(syncResult.added, "example", "examples")} and sorted {syncResult.categorised} into a kind.
            {syncResult.backfillBefore ? ` The library now reaches back to ${formatDate(syncResult.backfillBefore)}.` : ""}
            {syncResult.done ? " Every past reply in range has now been read." : " There is more to read; the nightly run carries on, or press Sync now again."}
          </p>
        )}
        {syncResult?.walkError && (
          <p className={`mt-1 text-xs ${AMBER_TEXT}`}>{syncResult.walkError} The replies already in the library were still sorted.</p>
        )}
        {syncResult?.categoriseError && <p className={`mt-1 text-xs ${AMBER_TEXT}`}>{syncResult.categoriseError}</p>}
      </section>

      {data && (
        <div className="mb-3 flex flex-wrap gap-2" aria-label="Library by kind of email">
          {data.categories.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={category === c.key}
              onClick={() => {
                setCategory(category === c.key ? "" : c.key);
                setOffset(0);
              }}
              className={chipCls(category === c.key)}
            >
              {c.label} <span className="opacity-70">({c.count})</span>
            </button>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          aria-label="Kind of email"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setOffset(0);
          }}
          className={`${inputCls} sm:max-w-xs`}
        >
          <option value="">Every kind</option>
          {data?.categories.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          placeholder="Search the library..."
          aria-label="Search the library"
          className={`${inputCls} sm:max-w-xs`}
        />
      </div>

      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}

      {loading && !data ? (
        <Loader rows={4} />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          {error}
        </p>
      ) : !data || data.examples.length === 0 ? (
        <EmptyState message={search.trim() || category ? "No library examples match." : "The library is empty."} hint={!search.trim() && !category ? "Press Sync now to read the office's sent replies." : undefined} />
      ) : (
        <>
          <ul className="space-y-3">
            {data.examples.map((e) => (
              <li key={e.id} data-testid={`example-${e.id}`} className="rounded-ele border border-ele-hairline bg-ele-surface p-4 text-sm">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-ele-body">
                    Sent {formatDate(e.sentAt)} · {e.categoryLabel}
                    {e.attachmentNames.length > 0 ? ` · Attached: ${e.attachmentNames.join(", ")}` : ""}
                  </span>
                  <ConfirmButton
                    onConfirm={() => void remove(e.id)}
                    busy={removingId === e.id}
                    busyLabel="Removing..."
                    className={`${ROW_ACTION} text-ele-body`}
                    title="Remove this example from the library"
                  />
                </div>
                <Excerpt label="They wrote" text={e.theyWrote} />
                <Excerpt label="The office replied" text={e.officeReplied} />
              </li>
            ))}
          </ul>
          <Pager offset={offset} total={data.total} shown={data.examples.length} pageSize={PAGE_SIZE} onOffset={setOffset} noun={["message", "messages"]} />
        </>
      )}

      <ForgetAddress onForgotten={() => void load()} />
    </div>
  );
}

function Excerpt({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-2">
      <span className={labelCls}>{label}</span>
      <p className="line-clamp-4 whitespace-pre-wrap text-ele-body">{text}</p>
    </div>
  );
}

// Erasure: every library example, inbound record and standard line naming one address, through the one erasure path
// (Session ASQ, Stream G, Item 3), so a record kept under a legal hold is left and counted. The confirm names the address.
function ForgetAddress({ onForgotten }: { onForgotten: () => void }) {
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const address = email.trim().toLowerCase();

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.emailEngine.forgetAddress(address);
      const held = res.held ? ` ${plural(res.held, "record was kept as it is", "records were kept as they are")} under a legal hold.` : "";
      setResult(
        `Removed ${plural(res.examplesRemoved, "library example", "library examples")}, ${plural(res.inboundRemoved, "inbound record", "inbound records")} and ${plural(res.standardLinesRemoved ?? 0, "standard line", "standard lines")} naming ${address}.${held} Emails from this address dated up to the erasure will not be recorded again; an email they send after the erasure is recorded as a new enquiry.`,
      );
      setOpen(false);
      setEmail("");
      onForgotten();
    } catch (err) {
      setError(friendlyApiError(err, "Could not forget that address. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${sectionCls} mt-6`}>
      <h2 className="font-heading text-lg text-ele-strong">Forget an email address</h2>
      <p className="mt-1 text-xs text-ele-body">
        When someone asks for their details to be removed, this erases every library example, inbound record and standard line naming their
        address (a record kept with work it raised keeps only the work, and anything under a legal hold is left). Their earlier emails are not
        recorded again; an email they send after the erasure is recorded as a new enquiry.
      </p>
      <form
        className="mt-3 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!address.includes("@")) {
            setError("Enter the full email address.");
            return;
          }
          setError(null);
          setResult(null);
          setOpen(true);
        }}
      >
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" aria-label="Email address to forget" className={`${inputCls} sm:max-w-xs`} />
        <Button type="submit" variant="destructive" size="sm">
          Forget this address
        </Button>
      </form>
      {result && (
        <p role="status" className="mt-2 text-xs text-ele-body">
          {result}
        </p>
      )}
      {error && !open && (
        <p role="alert" className={`mt-2 text-xs ${RED_TEXT}`}>
          {error}
        </p>
      )}
      <ConfirmDeleteModal
        open={open}
        variant="destructive"
        title="Forget this email address?"
        message={`Erase every library example, inbound record and standard line naming ${address}? This cannot be undone.`}
        confirmLabel="Forget it"
        busy={busy}
        error={open ? error : null}
        onConfirm={() => void confirm()}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

// ---- Draft quality ----------------------------------------------------------------------------------------------------

export function QualityTab() {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<EmailQualityReport | null>(null);
  const [lines, setLines] = useState<EmailStandardLineRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [q, l] = await Promise.all([api.emailEngine.quality(days), api.emailEngine.standardLines()]);
      setReport(q);
      setLines(l.lines);
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the draft quality report."));
    }
  }, [days]);
  useEffect(() => {
    void load();
  }, [load]);

  async function act(id: string, run: () => Promise<unknown>, fallback: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await run();
      await load();
    } catch (err) {
      setActionError(friendlyApiError(err, fallback));
    } finally {
      setBusyId(null);
    }
  }

  const decided = lines.filter((l) => l.status === "accepted" || l.status === "rejected");

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-ele-body">Drafts made in the last</span>
        {[7, 30].map((d) => (
          <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)} className={chipCls(days === d)}>
            {d} days
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}
      {!report && !error ? (
        <Loader rows={5} />
      ) : report ? (
        <>
          <div className="mb-6 overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm">
              <colgroup>
                <col style={{ width: "28%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="px-3 py-2">Kind of email</th>
                  <th className="px-3 py-2 text-right">Drafted</th>
                  <th className="px-3 py-2 text-right">Sent unchanged</th>
                  <th className="px-3 py-2 text-right">Small edits</th>
                  <th className="px-3 py-2 text-right">Heavily rewritten</th>
                  <th className="px-3 py-2 text-right">Discarded</th>
                  <th className="px-3 py-2 text-right">Not yet sent</th>
                </tr>
              </thead>
              <tbody>
                {report.categories.map((c) => (
                  <tr key={c.category} className="border-t border-ele-purple-accent">
                    <td className="truncate px-3 py-2" title={c.label}>
                      {c.label}
                    </td>
                    <td className="px-3 py-2 text-right">{c.drafted}</td>
                    <td className="px-3 py-2 text-right">{c.unchanged}</td>
                    <td className="px-3 py-2 text-right">{c.smallEdits}</td>
                    <td className="px-3 py-2 text-right">{c.heavilyRewritten}</td>
                    <td className="px-3 py-2 text-right">{c.discarded}</td>
                    <td className="px-3 py-2 text-right">{c.notYetSent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section className={sectionCls}>
            <h2 className="font-heading text-lg text-ele-strong">Proposed standard lines</h2>
            <p className="mt-1 text-xs text-ele-body">
              Sentences the office keeps adding to drafts of the same kind. An accepted line is added to every future draft of that kind.
              A proposed banned phrase is a sentence the office keeps taking out of drafts: accepting it adds it to the banned phrases on the
              Reply templates page, so drafts that use it are held back. Nothing is applied until you accept.
            </p>
            {report.proposedStandardLines.length === 0 ? (
              <p className="mt-3 text-sm text-ele-body">No lines proposed at the moment.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {report.proposedStandardLines.map((p) => (
                  <li key={p.id} data-testid={`proposed-${p.id}`} className="rounded-ele border border-ele-hairline p-3 text-sm">
                    {p.kind === "banned_phrase" && <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-blue">Proposed banned phrase</p>}
                    <p className="whitespace-pre-wrap text-ele-strong">{p.text}</p>
                    <p className="mt-1 text-xs text-ele-body">
                      {p.label} · {p.kind === "banned_phrase" ? "taken out by hand" : "added by hand"} {plural(p.occurrences, "time", "times")}, last {formatDate(p.lastSeenAt)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-4">
                      <ConfirmButton
                        label="Accept"
                        confirmLabel={p.kind === "banned_phrase" ? `Ban this phrase for every draft?` : `Accept for ${p.label}?`}
                        busy={busyId === p.id}
                        busyLabel="Saving..."
                        className={`${ROW_ACTION} text-ele-green`}
                        armedClassName="!text-ele-green font-semibold"
                        onConfirm={() => void act(p.id, () => api.emailEngine.decideStandardLine(p.id, "accept"), "Could not accept that line.")}
                      />
                      <ConfirmButton
                        label="Reject"
                        confirmLabel={`Reject for ${p.label}?`}
                        busy={busyId === p.id}
                        busyLabel="Saving..."
                        className={`${ROW_ACTION} text-ele-body`}
                        onConfirm={() => void act(p.id, () => api.emailEngine.decideStandardLine(p.id, "reject"), "Could not reject that line.")}
                      />
                      <ConfirmButton
                        busy={busyId === p.id}
                        className={`${ROW_ACTION} text-ele-body`}
                        title="Delete this proposed line"
                        onConfirm={() => void act(p.id, () => api.emailEngine.removeStandardLine(p.id), "Could not delete that line.")}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={sectionCls}>
            <h2 className="font-heading text-lg text-ele-strong">Accepted and rejected lines</h2>
            {decided.length === 0 ? (
              <p className="mt-3 text-sm text-ele-body">None yet.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {decided.map((l) => (
                  <li key={l.id} data-testid={`line-${l.id}`} className="flex flex-col gap-2 rounded-ele border border-ele-hairline p-3 text-sm sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      {l.kind === "banned_phrase" && <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-blue">Banned phrase</p>}
                      <p className="whitespace-pre-wrap text-ele-strong">{l.text}</p>
                      <p className="mt-1 text-xs text-ele-body">
                        {l.label} ·{" "}
                        {l.kind === "banned_phrase"
                          ? l.status === "accepted"
                            ? "Accepted: on the style guide's banned phrases"
                            : "Rejected: never banned"
                          : l.status === "accepted"
                            ? "Accepted: added to every future draft of this kind"
                            : "Rejected: never added"}
                        {l.decidedAt ? `, ${formatDate(l.decidedAt)}` : ""}
                      </p>
                    </div>
                    <ConfirmButton
                      busy={busyId === l.id}
                      className={`${ROW_ACTION} text-ele-body`}
                      title="Delete this line"
                      onConfirm={() => void act(l.id, () => api.emailEngine.removeStandardLine(l.id), "Could not delete that line.")}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

// ---- One message ------------------------------------------------------------------------------------------------------

export function EmailEngineMessagePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState<EmailEngineMessageDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setMessage(await api.emailEngine.message(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That message could not be found.</p>;
  if (state === "error" || !message) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this message. Please try again.</p>;

  const pill = outcomeDisplay(message);
  const canDraftFresh = !message.draft && (message.outcome === "needs_you" || message.outcome === "no_reply_needed");

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        title={message.subject?.trim() || "(no subject)"}
        backTo="/office/mail?tab=all"
        backLabel="Back to Mail"
        status={<StatusPill word={pill.word} tone={pill.tone} />}
        subtitle={`${senderLine(message)} · Received ${formatDateTime(message.receivedAt)}`}
        className="mb-4"
      />

      <section className={sectionCls}>
        <h2 className="mb-3 font-heading text-lg text-ele-strong">The email</h2>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="From" value={senderLine(message)} />
          <Field label="Received" value={formatDateTime(message.receivedAt)} />
          <Field label="To" value={message.to.join(", ") || "-"} />
          <Field label="Cc" value={message.cc.join(", ") || "-"} />
          <Field label="Outcome" value={`${pill.word}${message.outcomeReason ? `: ${message.outcomeReason}` : ""}`} />
          <Field label="Kind of email" value={message.categoryLabel ?? "Not yet sorted"} />
          <div>
            <dt className={labelCls}>Gmail</dt>
            <dd className="mt-0.5">
              <a href={gmailThreadHref(message.gmailThreadId)} target="_blank" rel="noopener noreferrer" className="text-ele-blue underline-offset-2 hover:underline">
                Open in Gmail
              </a>
            </dd>
          </div>
          {message.enquiryId && (
            <div>
              <dt className={labelCls}>Enquiry</dt>
              <dd className="mt-0.5">
                <Link to={`/office/enquiries/${encodeURIComponent(message.enquiryId)}`} className="text-ele-blue underline-offset-2 hover:underline">
                  Open the enquiry
                </Link>
              </dd>
            </div>
          )}
        </dl>
        {message.lastError && <p className={`mt-3 text-xs ${AMBER_TEXT}`}>The engine's last attempt hit a problem: {message.lastError}</p>}
        {message.readIssue && (
          <p data-testid="read-issue" className={`mt-3 text-sm ${AMBER_TEXT}`}>
            {message.readIssue}
          </p>
        )}
        {/* Session ATX, Item 2: what the message said, stored when it was recorded (quoted history and signature removed). */}
        {message.body && (
          <div className="mt-4">
            <p className={labelCls}>What they wrote</p>
            <pre data-testid="message-body" className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong">
              {message.body.text}
            </pre>
            {message.body.truncated && <p className="mt-1 text-xs text-ele-body">This is the start of a long message; open it in Gmail for the rest.</p>}
          </div>
        )}
        {message.attachments && message.attachments.length > 0 && (
          <p data-testid="message-attachments" className="mt-2 text-xs text-ele-body">
            Attached: {message.attachments.map((a) => a.filename).join(", ")}
          </p>
        )}
      </section>

      {/* Session ATZ (Item 2): a filed message, its Gmail label, and the invoices a remittance names (read live). */}
      {message.filed && (
        <section className={sectionCls} data-testid="message-filed">
          <h2 className="mb-2 font-heading text-lg text-ele-strong">Filed</h2>
          <p className="text-sm text-ele-body">
            Filed under <span className="text-ele-strong">{message.filed.label ?? "its label"}</span> in Gmail
            {message.filed.labelledInGmail ? "." : ". The label has not reached Gmail yet; the engine tries again on its next check."}
          </p>
          {message.filed.invoices.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {message.filed.invoices.map((n) => (
                <li key={n.invoiceNumber} className="text-ele-body">
                  <span className="text-ele-blue">{n.invoiceNumber}</span>:{" "}
                  {n.invoices.length === 0
                    ? "no invoice with this number is in the platform's copy of Xero."
                    : n.invoices.map((i) => `${i.customerName ?? "invoice"}, £${i.amount}, ${i.status} in Xero`).join("; ")}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-ele-body">Whether an invoice is paid is always read from Xero, never from this email.</p>
        </section>
      )}
      {message.lane === "work" && message.workOrderProposalId && (
        <section className={sectionCls}>
          <h2 className="mb-2 font-heading text-lg text-ele-strong">Work order</h2>
          <p className="text-sm text-ele-body">
            This is a property platform's work order email. It is on the{" "}
            <Link to="/office/mail?tab=work" className="text-ele-green hover:underline">
              Work orders
            </Link>{" "}
            tab in Mail, ready to raise as a call-out or repair.
          </p>
        </section>
      )}

      <section className={sectionCls}>
        <h2 className="mb-3 font-heading text-lg text-ele-strong">The draft</h2>
        {message.draft ? (
          <>
            <p className="mb-2 text-sm" data-testid="draft-where">
              {message.inGmail ? (
                <span className="text-ele-strong">In Gmail: the draft is waiting in the conversation for you to check and send.</span>
              ) : (
                <span className={AMBER_TEXT}>
                  On this page only
                  {message.personDraftPresent
                    ? ": someone had already started a reply in this conversation in Gmail, so the engine did not add a second draft."
                    : "."}
                </span>
              )}
            </p>
            {message.draft.instruction && <p className="mb-2 text-xs text-ele-body">Drafted again with the instruction: "{message.draft.instruction}"</p>}
            <DraftSource
              kind={message.draft.kind ?? message.draftKind}
              templateKey={message.draft.template?.key ?? message.templateKey}
              templateVersion={message.draft.template?.version ?? message.templateVersion}
              templateId={message.draft.template?.id ?? message.templateId}
              missText={message.draft.templateMiss?.text}
              signature={message.draft.signature}
            />
            <pre data-testid="draft-body" className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong">
              {message.draft.body}
            </pre>
            {message.draft.attachments.length > 0 && (
              <p className="mt-2 text-xs text-ele-body">Attached: {message.draft.attachments.map((a) => ATTACHMENT_LABEL[a] ?? a).join(", ")}</p>
            )}
            <Checks validation={message.draft.validation} />
          </>
        ) : (
          <p className="text-sm text-ele-body">No draft was written for this message.</p>
        )}
      </section>

      {/* Session BCR6 Phase 4: how the investigating agent wrote the live draft, and what it would have written beside the classic one. */}
      {message.agentRun && <AgentRunSection run={message.agentRun} />}
      {message.agentShadow && <AgentShadowSection shadow={message.agentShadow} />}

      {message.facts && <FactsBlock facts={message.facts} />}
      {message.examples.length > 0 && <ExamplesBlock examples={message.examples} />}

      {message.sent && (
        <section className={sectionCls}>
          <h2 className="mb-3 font-heading text-lg text-ele-strong">What was sent</h2>
          <p className="mb-2 text-sm text-ele-body">
            Sent {formatDateTime(message.sent.at)}
            {message.editClass ? ` · ${EDIT_KIND_LABEL[message.editClass] ?? message.editClass}` : ""}
          </p>
          <pre className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong">{message.sent.text ?? ""}</pre>
        </section>
      )}

      {message.outcome !== "pending" && (message.draft || canDraftFresh) && (
        <DraftComposer key={message.id} messageId={message.id} mode={message.draft ? "redraft" : "draft"} onCommitted={() => void load({ silent: true })} />
      )}

      <DeleteMessage message={message} onDeleted={() => navigate("/office/mail?tab=all")} />
    </div>
  );
}

// Session AUA (email system Phase 3): where the draft's wording came from (an approved template, or the labelled free
// draft with why no template was used) and the signature it carries. Every surface that shows a draft says this.
function DraftSource({
  kind,
  templateKey,
  templateVersion,
  templateTitle,
  templateId,
  missText,
  signature,
}: {
  kind?: "template" | "free" | "agent" | null;
  templateKey?: string | null;
  templateVersion?: number | null;
  templateTitle?: string | null;
  templateId?: string | null;
  missText?: string | null;
  signature?: string | null;
}) {
  const label = draftSourceLabel({ kind, templateKey, templateVersion, templateTitle });
  if (!label && !signature) return null;
  return (
    <div className="mb-2 text-xs text-ele-body" data-testid="draft-source">
      {label && (
        <p>
          {kind === "template" && templateId ? (
            <Link to={`/office/reply-templates/${encodeURIComponent(templateId)}`} className="text-ele-blue underline-offset-2 hover:underline">
              {label}
            </Link>
          ) : (
            <span className={kind === "free" || kind === "agent" ? AMBER_TEXT : "text-ele-strong"}>{label}</span>
          )}
          {kind === "free" && missText ? <span>: {missText}</span> : null}
        </p>
      )}
      {signature && <p>Signature: {signature}</p>}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className={labelCls}>{label}</dt>
      <dd className="mt-0.5 break-words text-ele-strong">{value}</dd>
    </div>
  );
}

// The checks a draft was put through. A failure shows the offending line as a highlighted quote and says what is wrong.
function Checks({ validation }: { validation: { passed?: boolean; failures?: EmailEngineValidationFailure[]; gate?: EmailEngineGateLog } | null }) {
  if (!validation) return null;
  const cut = validation.gate?.removed ?? [];
  // Session BCR2: in log only (the default since D-BCR1-1) the gate recorded these but removed nothing; the office reads them
  // as warnings about the draft it is looking at. A record with no mode was written while the gate always enforced.
  const logOnly = validation.gate?.mode === "log_only";
  return (
    <>
      <FailureList passed={validation.passed ?? false} failures={validation.failures ?? []} />
      {cut.length > 0 && (
        <div className="mt-3" data-testid="gate-cut" data-gate-mode={logOnly ? "log_only" : "enforce"}>
          <p className="text-sm font-semibold text-ele-strong">
            {logOnly
              ? `The sentence gate (log only) found ${plural(cut.length, "statement", "statements")} that nothing in the records or the customer's message supports. ${cut.length === 1 ? "It is" : "They are"} still in the draft: check ${cut.length === 1 ? "it" : "them"} before sending.`
              : `${plural(cut.length, "statement was", "statements were")} taken out of the draft because nothing in the records or the customer's message supports ${cut.length === 1 ? "it" : "them"}:`}
          </p>
          <ul className="mt-2 space-y-2">
            {cut.map((c, i) => (
              <li key={i} className="text-sm">
                <blockquote className="border-y border-ele-hairline py-1">
                  <mark className="rounded bg-ele-amber-wash px-1 text-ele-wash-ink">{c.sentence}</mark>
                </blockquote>
                <p className="mt-1 text-ele-body">{c.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function FailureList({ passed, failures }: { passed: boolean; failures: EmailEngineValidationFailure[] }) {
  if (passed && failures.length === 0) {
    return (
      <p data-testid="checks-passed" className="mt-3 text-sm text-ele-green">
        Passed every check.
      </p>
    );
  }
  return (
    <div className="mt-3" data-testid="checks-failed">
      <p className={`text-sm font-semibold ${RED_TEXT}`}>
        {failures.length === 0 ? "This draft did not pass its checks." : `This draft failed ${plural(failures.length, "check", "checks")}:`}
      </p>
      <ul className="mt-2 space-y-3">
        {failures.map((f, i) => (
          <li key={`${f.check}-${i}`} className="text-sm">
            <span className={labelCls}>{CHECK_LABEL[f.check] ?? f.check}</span>
            <blockquote className="border-y border-ele-hairline py-1">
              <mark className="rounded bg-ele-amber-wash px-1 text-ele-wash-ink">{f.line}</mark>
            </blockquote>
            <p className="mt-1 text-ele-body">{f.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FactsBlock({ facts }: { facts: EmailEngineFacts }) {
  return (
    <section className={sectionCls}>
      <h2 className="font-heading text-lg text-ele-strong">The facts the draft was allowed to use</h2>
      <p className="mt-1 text-xs text-ele-body">Every price, reference, date and promise in a draft must come from this list.</p>
      <dl className="mt-3 grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {facts.lines.map((l) => (
          <div key={l.key} className="contents">
            <dt className="text-ele-body">{l.label}</dt>
            <dd className="break-words text-ele-strong">{l.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ExamplesBlock({ examples }: { examples: EmailEngineExample[] }) {
  return (
    <section className={sectionCls}>
      <h2 className="font-heading text-lg text-ele-strong">Past replies it was written from</h2>
      <ul className="mt-3 space-y-3">
        {examples.map((e) => (
          <li key={e.id} className="rounded-ele border border-ele-hairline p-3 text-sm">
            <p className="text-xs text-ele-body">Sent {formatDate(e.sentAt)}</p>
            <Excerpt label="They wrote" text={e.theyWrote} />
            <Excerpt label="The office replied" text={e.officeReplied} />
          </li>
        ))}
      </ul>
    </section>
  );
}

// "Draft again" (with an instruction) or "Draft a reply" (a message the engine did not draft). Always a preview first:
// the office reads the text, its checks and its facts, and only then puts exactly that text in Gmail.
export function DraftComposer({ messageId, mode, onCommitted }: { messageId: string; mode: "redraft" | "draft"; onCommitted: () => void }) {
  const [instruction, setInstruction] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<{ candidateId: string; draft: EmailEngineDraftPreview } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // Session BCR3: the OLD-THREAD OVERRIDE from the page, not only from chat. Offered only after the server refused a preview as
  // a very late fault or complaint (oldThreadOverrideAvailable); it needs a reason (recorded in the audit log) and the instruction.
  const [oldThreadOffered, setOldThreadOffered] = useState(false);
  const [oldThread, setOldThread] = useState(false);
  const [oldThreadReason, setOldThreadReason] = useState("");
  const trimmed = instruction.trim();
  const reasonOk = oldThreadReason.trim().length >= 10;
  const override = oldThread && reasonOk ? { allowOldThread: true, allowOldThreadReason: oldThreadReason.trim() } : {};

  async function askPreview() {
    setPreviewing(true);
    setError(null);
    setDone(null);
    setPreview(null);
    try {
      const res: EmailEngineDraftResponse =
        mode === "redraft"
          ? await api.emailEngine.redraft(messageId, { instruction: trimmed, preview: true, ...override })
          : await api.emailEngine.draft({ inboundEmailId: messageId, instruction: trimmed || undefined, preview: true, ...override });
      if (res.preview) setPreview({ candidateId: res.candidateId, draft: res.draft });
    } catch (err) {
      if (err instanceof ApiError && err.body?.oldThreadOverrideAvailable === true) setOldThreadOffered(true);
      setError(friendlyApiError(err, "A draft could not be written. Please try again."));
    } finally {
      setPreviewing(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      const res =
        mode === "redraft"
          ? await api.emailEngine.redraft(messageId, { instruction: trimmed, preview: false, candidateId: preview.candidateId })
          : await api.emailEngine.draft({ preview: false, candidateId: preview.candidateId });
      if (!res.preview) {
        setDone(
          res.delivery?.gmailDraftId
            ? "The draft is in Gmail, in the conversation, ready for you to check and send."
            : `The draft was saved on this page only. ${res.outcomeReason}`,
        );
      }
      setPreview(null);
      setInstruction("");
      onCommitted();
    } catch (err) {
      setError(friendlyApiError(err, "The draft could not be put in Gmail. Please try again."));
    } finally {
      setCommitting(false);
    }
  }

  const title = mode === "redraft" ? "Draft again" : "Draft a reply";
  return (
    <section className={sectionCls} aria-label={title}>
      <h2 className="font-heading text-lg text-ele-strong">{title}</h2>
      <p className="mt-1 text-xs text-ele-body">
        {mode === "redraft"
          ? 'Say how the draft should change, for example "make it shorter" or "offer a visit next week". You will see the new draft before anything reaches Gmail.'
          : "The engine did not draft this message. Ask it for a reply; you will see it before anything reaches Gmail."}
      </p>
      <label className="mt-3 block">
        <span className={labelCls}>{mode === "redraft" ? "Instruction" : "Instruction (optional)"}</span>
        <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} maxLength={4000} className={inputCls} aria-label="Instruction for the draft" />
      </label>
      {oldThreadOffered && (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="old-thread-override">
          <label className="flex items-start gap-2 text-sm text-ele-strong">
            <input type="checkbox" checked={oldThread} onChange={(e) => setOldThread(e.target.checked)} className="mt-1" />
            <span>Write to this old fault or complaint anyway. The reply will say only what your instruction asks, with no apology for the delay added.</span>
          </label>
          {oldThread && (
            <label className="mt-2 block">
              <span className={labelCls}>Why (recorded in the audit log, at least 10 characters)</span>
              <textarea value={oldThreadReason} onChange={(e) => setOldThreadReason(e.target.value)} rows={2} maxLength={500} className={inputCls} aria-label="Reason for writing to the old conversation" />
            </label>
          )}
          {oldThread && !trimmed && <p className="mt-1 text-xs text-ele-body">Say in the instruction what the reply should say.</p>}
        </div>
      )}
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={() => void askPreview()} disabled={previewing || committing || (mode === "redraft" && !trimmed) || (oldThread && (!reasonOk || !trimmed))}>
          {previewing ? "Writing..." : mode === "redraft" ? "Preview the new draft" : "Preview a reply"}
        </Button>
      </div>

      {preview && (
        <div className="mt-4 rounded-ele border border-ele-hairline p-4" data-testid="draft-preview">
          {preview.draft.body ? (
            <>
              <span className={labelCls}>The new draft ({preview.draft.categoryLabel})</span>
              <DraftSource
                kind={preview.draft.draftKind}
                templateKey={preview.draft.template?.key}
                templateVersion={preview.draft.template?.version}
                templateTitle={preview.draft.template?.title}
                templateId={preview.draft.template?.id}
                missText={preview.draft.templateMiss?.text}
                signature={preview.draft.signature ?? COMPANY_SIGNATURE_LABEL}
              />
              <pre className="whitespace-pre-wrap break-words rounded-ele bg-ele-inset p-3 font-body text-sm text-ele-strong">{preview.draft.body}</pre>
              {preview.draft.attachments.length > 0 && (
                <p className="mt-2 text-xs text-ele-body">Attached: {preview.draft.attachments.map((a) => ATTACHMENT_LABEL[a] ?? a).join(", ")}</p>
              )}
              <FailureList passed={preview.draft.passedChecks === true} failures={preview.draft.failures} />
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-ele-blue">The facts it was allowed to use</summary>
                <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  {preview.draft.facts.lines.map((l) => (
                    <div key={l.key} className="contents">
                      <dt className="text-ele-body">{l.label}</dt>
                      <dd className="break-words text-ele-strong">{l.value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <ConfirmButton
                  label="Put this draft in Gmail"
                  confirmLabel="Put this draft in Gmail?"
                  busy={committing}
                  busyLabel="Putting it in Gmail..."
                  className="rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green"
                  armedClassName="!text-ele-green font-semibold"
                  onConfirm={() => void commit()}
                />
                <button type="button" onClick={() => setPreview(null)} className={`${ROW_ACTION} text-ele-body`} disabled={committing}>
                  Discard this preview
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-ele-body">The engine did not write a draft: {preview.draft.reason ?? "no reason was given."}</p>
          )}
        </div>
      )}
      {done && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          {done}
        </p>
      )}
      {error && (
        <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteMessage({ message, onDeleted }: { message: EmailEngineMessageDetail; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.emailEngine.removeMessage(message.id);
      onDeleted();
    } catch (err) {
      // The server refuses (409) while the message is being decided, or while the hourly check would only record it
      // again, and says when it can be deleted. That sentence is shown as it is.
      setError(friendlyApiError(err, "Could not delete this record."));
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Delete this record
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant="destructive"
        title="Delete this record?"
        message="This removes the engine's record of this message, its outcome and its draft from the platform. The email itself stays in Gmail. This cannot be undone."
        confirmLabel="Delete record"
        busy={busy}
        error={error}
        onConfirm={() => void confirm()}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}
